import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {RefreshIndicator} from '@ELib/components/ETable/RefreshIndicator';
import {ETooltip} from '@ELib/components/ETooltip';
import {ECheckbox} from '@ELib/components/form/ECheckbox';
import {toggleItemInList, useSelection} from '@ELib/functions/selection';
import {showSnackbar} from '@ELib/functions/snackbar';
import {classy, isNonNullable} from '@ELib/utilities/HelperFunctions';
import {isInputElement} from '@ELib/utilities/dom';
import {useArrowKeysLoadTriggerHelper} from '@ELib/utilities/hooks/arrowKeyLoadTriggerHelper';
import {stopEvent} from '@ELib/utilities/hooks/keyboardHandler';
import {generateRandomUnsafeId} from '@ELib/utilities/id';
import {Order, ShortKeyActionType} from '@ELib/utilities/types';

import {SnackbarType} from '../Snackbar';

import {ColumnsCustomizeDialog} from './ColumnsCustomizeDialog/ColumnsCustomizeDialog';
import {LoadTrigger} from './LoadTrigger';
import {TBodyRow} from './TBodyRow';
import {THead} from './THead';
import {ColumnElement} from './columnState';
import {expansionColumn} from './columns';
import {handleDragEnd, handleDragStart} from './drag-handlers';
import {isActionActiveForAllRows, useActionsHandler} from './hooks';
import './react-table@8';
import {StyledETable, StyledETableScrollWrapper} from './styles';
import {
  ETableActiveItem,
  ETableCell,
  ETableProps,
  ETableRow,
  ETableRowActionHandler,
  ETableRowClickHandler,
  ETableRowDragHandler,
  ETableRowDragParams,
  ETableRowID,
  getActiveItemIdOrParentId,
  isActiveRowChildItem,
} from './types';
import {
  getSelectionAndExpanderKeys,
  useColumnsConfig,
} from './useColumnsConfig';
import {useETableVirtualizer} from './useETableVirtualizer';
import {useStableColumnWidths} from './useStableColumnWidths';
import {findTableRow, isEqualActiveItem, isOrderIdentical} from './utilities';

/**
 * Table component based on TanStack Table https://tanstack.com/table/v8.
 *
 * Selection:
 * TanStack Table has native support for checkboxes, but implementing this in a
 * custom way seemed easier, especially for persisting the selection after
 * sorting and filtering.
 */
// eslint-disable-next-line max-statements
export function ETable<Key extends string, Sort extends string>({
  columns: userColumns,
  data: userData,
  order,
  activeItem: userActiveItem,
  onActiveItemChange,
  selectedItems: userSelectedItems,
  onSelectedItemsChange,
  expandedItems: userExpandedItems,
  onExpandedItemsChange,
  onOrderChange,
  hasMoreBefore,
  hasMoreAfter,
  onLoadMoreBefore,
  onLoadMoreAfter,
  placeholder,
  scrollIntoView = true,
  testId,
  forceCheckboxes,
  hideHeader,
  persistenceId,
  disableVirtualization,
  refreshIndicator,
}: ETableProps<Key, Sort>): React.ReactElement {
  type RowType = Row<ETableRow>;

  const [tableId] = useState(generateRandomUnsafeId());
  const {t} = useTranslation('lib');
  const noContentPlaceholder = placeholder ?? t('placeholder.noContent');
  const userTriggeredActiveItemChange = useRef(false);

  /**
   * Data to be shown in the table.
   *
   * The generic type `Key extends string` is relaxed to `string` as the narrow
   * type is not needed internally anymore. Additionally, the columns are
   * relaxed to `string` as well as they are extended with special columns.
   * Both `data` and `columns` must have the same generic key type.
   */
  const data = userData as ETableRow[];

  /** Reference to the table element in the DOM. */
  const tableRef = useRef<HTMLTableElement>(null);
  /** Reference to the scrollable element that wraps the table. */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentCheckboxId = useRef<string>(undefined);
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);

  /**
   * Scrolls to a row that is not rendered because it is outside the virtual
   * window. Assigned after the virtualizer hook below; kept in a ref as
   * `scrollRowIntoView` has to be declared before the table instance exists.
   */
  const scrollToVirtualRowRef = useRef<
    (rowId: string, align?: 'auto' | 'center') => void
  >(() => undefined);

  const scrollRowIntoView = useCallback(
    (rowDataId?: ETableActiveItem): void => {
      if (!scrollIntoView || !rowDataId || typeof rowDataId === 'object') {
        return;
      }
      if (
        userTriggeredActiveItemChange.current ||
        data.findIndex((it) => it.id === rowDataId) === 0
      ) {
        userTriggeredActiveItemChange.current = false;

        const row = findTableRow(tableRef.current, rowDataId);
        if (row) {
          row.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        } else {
          scrollToVirtualRowRef.current(rowDataId);
        }
      } else {
        // setTimeout is necessary if active item is set initially from the outside
        setTimeout(() => {
          const row = findTableRow(tableRef.current, rowDataId);
          if (row) {
            row.scrollIntoView({
              behavior: 'instant',
              block: 'center',
              inline: 'nearest',
            });
          } else {
            // mount the row through the virtualizer, then center it exactly
            scrollToVirtualRowRef.current(rowDataId, 'center');
            requestAnimationFrame(() =>
              findTableRow(tableRef.current, rowDataId)?.scrollIntoView({
                behavior: 'instant',
                block: 'center',
                inline: 'nearest',
              })
            );
          }
        }, 250);
      }
    },
    // keep it as before move to webapp-lib
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableRef]
  );

  const [activeItem, setActiveItem] = useState<ETableActiveItem>();

  // Detect whether at least one row needs a checkbox or expansion, or extra actions
  const {isSelectable, hasExpansion} = useMemo(() => {
    /* eslint-disable @typescript-eslint/no-shadow */
    const isSelectable = data.some(({options}) => options?.isSelectable);
    const hasExpansion = data.some(({options}) => Boolean(options?.expansion));

    /* eslint-enable @typescript-eslint/no-shadow */
    return {isSelectable, hasExpansion};
  }, [data]);

  const {selectedItemIds, selectedItemsIdsAsString, setSelectedItemIds} =
    useSelection(userSelectedItems, false, onSelectedItemsChange);
  const keepSelection = useRef(false);

  /** Highlights the selected row and ensures it is within the viewport. */
  const activateRow = useCallback(
    (newActiveItem?: ETableActiveItem) => {
      setActiveItem(newActiveItem);
      const newActiveRowId = isActiveRowChildItem(newActiveItem)
        ? newActiveItem.childId
        : newActiveItem;
      scrollRowIntoView(newActiveRowId);

      // Make sure the newActiveItem is included in the selection.
      // An item cannot be active without also being in the selection.
      const activeItemIdOrActiveItemParentId =
        getActiveItemIdOrParentId(newActiveItem);
      setSelectedItemIds((currentSelectedItemIds: Set<string>) => {
        let newSet: Set<string>;
        if (activeItemIdOrActiveItemParentId) {
          newSet = keepSelection.current
            ? new Set(currentSelectedItemIds).add(
                activeItemIdOrActiveItemParentId
              )
            : new Set([activeItemIdOrActiveItemParentId]);
        } else {
          newSet = keepSelection.current
            ? new Set(currentSelectedItemIds)
            : new Set();
        }
        keepSelection.current = false;
        return newSet;
      });
    },
    [scrollRowIntoView, setSelectedItemIds]
  );

  const setNewActiveRow = useCallback(
    (row: RowType | undefined, childRow?: RowType) => {
      const newUserActiveItem =
        childRow && row
          ? {parentId: row.original.id, childId: childRow.original.id}
          : row?.original.id;

      userTriggeredActiveItemChange.current = true;
      if (onActiveItemChange) {
        onActiveItemChange(newUserActiveItem);
      } else {
        activateRow(newUserActiveItem);
      }
    },
    [activateRow, onActiveItemChange]
  );

  useEffect(() => {
    if (!isEqualActiveItem(activeItem, userActiveItem)) {
      activateRow(userActiveItem);
    }
    // set correct items when dependencies change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActiveItem]);

  const rowIds = data.map((row) => row.id).join(',');

  // match selected rows to new data (e.g. after sort or filter)
  useEffect(
    () => {
      setSelectedItemIds((currentSelectedItemIds) => {
        const verifiedSelectedItemIds = new Set<ETableRowID>();
        currentSelectedItemIds.forEach((selectedItemId) => {
          const itemForId = data.find(
            ({id, options}) => options?.isSelectable && id === selectedItemId
          );
          if (itemForId) {
            verifiedSelectedItemIds.add(selectedItemId);
          }
        });
        return verifiedSelectedItemIds;
      });
    },
    // `selectedItemIds` is missing intentionally to avoid useEffect loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowIds]
  );

  /** Callback that is executed when a checkbox on a body row is toggled. */
  const onCheckboxChange = useCallback(
    (id: string) => {
      if (selectedItemIds.has(id)) {
        const newSelectedRows = new Set(selectedItemIds);
        newSelectedRows.delete(id);
        setSelectedItemIds(newSelectedRows);
        const activeItemIdOrActiveItemParentId =
          getActiveItemIdOrParentId(activeItem);
        if (id === activeItemIdOrActiveItemParentId) {
          keepSelection.current = true;
          setNewActiveRow(undefined);
        }
      } else {
        setSelectedItemIds(new Set(selectedItemIds).add(id));
      }
      // the table loses focus on checkbox click. get it back so the user can use shortcuts again.
      if (currentCheckboxId.current) {
        const checkboxIdToFocus = currentCheckboxId.current;
        setTimeout(() => {
          document.getElementById(checkboxIdToFocus)?.focus();
        });
      } else {
        tableRef.current?.focus();
      }
    },
    // use 'selectedItemsIdsAsString' in place of `selectedItemIds` to detect changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemsIdsAsString, setSelectedItemIds, activeItem]
  );

  /**
   * Flag indicating whether all body row checkboxes are selected.
   * This information is used in the table head.
   */
  const hasAllItemsSelected = useMemo(() => {
    if (selectedItemIds.size === 0) {
      return false;
    }
    const numberOfItemsWithSelection = data.reduce(
      (num, row) => (row.options?.isSelectable ? num + 1 : num),
      0
    );
    return selectedItemIds.size === numberOfItemsWithSelection;
  }, [data, selectedItemIds.size]);

  /** Callback that is executed when the checkbox in the table header is toggled. */
  const toggleSelectAll = useCallback(() => {
    if (hasAllItemsSelected) {
      setSelectedItemIds(new Set());
      setNewActiveRow(undefined);
    } else {
      const allItemIdsWithSelection = data.reduce<string[]>((idList, item) => {
        if (item.options?.isSelectable) {
          return [...idList, item.id];
        }
        return idList;
      }, []);
      setSelectedItemIds(new Set(allItemIdsWithSelection));
    }
    // the table loses focus on checkbox click. get it back so the user can use shortcuts again.
    tableRef.current?.focus();
  }, [data, hasAllItemsSelected, setSelectedItemIds, setNewActiveRow]);

  /** Selection column definition (i.e. checkboxes). */
  const selectionColumn = useMemo(
    (): ColumnDef<ETableRow, ETableCell> => ({
      id: '_selection',
      header: () => (
        <ECheckbox
          // Value: false, indeterminate=null, or true
          className='no-margin'
          value={
            selectedItemIds.size === 0
              ? false
              : hasAllItemsSelected
                ? true
                : undefined
          }
          sparseGutters
          onChange={toggleSelectAll}
          onKeyDown={keyDownHandler}
        />
      ),
      enableSorting: false,
      cell: (ctx: CellContext<ETableRow, ETableCell>) => {
        if (!ctx.row.original.options?.isSelectable) {
          return null;
        }
        const itemId = ctx.row.original.id;
        const checkboxId = `${tableId}-checkbox-${itemId}`;
        return (
          <ECheckbox
            className='no-margin'
            id={checkboxId}
            value={selectedItemIds.has(itemId)}
            sparseGutters
            stopPropagation
            onChange={() => onCheckboxChange(itemId)}
            onFocus={() => {
              currentCheckboxId.current = checkboxId;
            }}
          />
        );
      },
      meta: {
        SubCell: () => '', // No selection on an expanded row
      },
    }),
    // `keyDownHandler` is missing because common dependencies should trigger necessary rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItemIds, hasAllItemsSelected, toggleSelectAll, onCheckboxChange]
  );

  /**
   * Definition of columns. This also patches checkboxes, expansion, and/or extra
   * actions for rows that need them.
   */
  const columns = useMemo(
    (): ColumnDef<ETableRow, ETableCell>[] => [
      ...(isSelectable ? [selectionColumn] : []),
      ...(hasExpansion ? [expansionColumn()] : []),
      ...userColumns.map(
        (col): ColumnDef<ETableRow, ETableCell> => ({
          header: () =>
            col.tooltipTitle ? (
              <ETooltip title={col.tooltipTitle}>
                <span>{col.label}</span>
              </ETooltip>
            ) : (
              col.label
            ),
          // an accessor function is used instead of `accessorKey` as the
          // latter would interpret dots in column names as deep paths
          accessorFn: (row) => row[col.name],
          id: col.name,
          enableSorting: Boolean(col.sortName),
          meta: {
            className: classy({
              alignRight: col.align === 'right',
              alignCenter: col.align === 'center',
            }),
            style: {
              width: col.width,
            },
          },
        })
      ),
    ],
    [isSelectable, selectionColumn, hasExpansion, userColumns]
  );

  const {
    initialColumnOrder,
    initialHiddenColumns,
    setColumnsStateByTableId,
    savedColumnConfig,
  } = useColumnsConfig({
    userColumns,
    persistenceId,
  });

  const handleColumnChanges = (newColumns?: ColumnElement[]) => {
    if (newColumns && persistenceId) {
      table.setColumnOrder([
        ...getSelectionAndExpanderKeys(),
        ...newColumns.map((column) => column.name),
      ]);

      // the whole visibility map must be set at once (columns that are not
      // listed, i.e. the special columns, default to visible)
      table.setColumnVisibility(
        Object.fromEntries(
          newColumns.map((column) => [column.name, column.active])
        )
      );

      setColumnsStateByTableId((currentState) => ({
        ...currentState,
        [persistenceId]: newColumns.map((column) => ({
          name: column.name,
          active: column.active,
          defaultHidden: column.defaultHidden,
        })),
      }));
    }
    setColumnDialogOpen(false);
  };

  const sorting = useMemo(() => {
    if (!order) {
      return [];
    }
    return order.map((orderItem) => ({
      id: userColumns.find((col) => col.sortName === orderItem.columnName)!
        .name as string,
      desc: orderItem.order === Order.Descending,
    }));
  }, [order, userColumns]);

  /**
   * Initial (!) expansion state (if any). It is important to distinguish this from the expansion
   * state at a later stage as the initial state is passed to `useTable` and later changes could
   * wrongly overwrite the actual state. Hence, this block intentionally omits any dependencies.
   */
  const initialExpansion = useMemo(
    () => Object.fromEntries(userExpandedItems?.map((id) => [id, true]) ?? []),
    // rule disabled due to the fact that we only want this effect to run on setup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // https://tanstack.com/table/v8/docs/api/core/table
  const table = useReactTable<ETableRow>({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: false,
    // v8 would pick the first sort direction from the cell values (desc for
    // non-strings); always start ascending, like react-table v7
    sortDescFirst: false,
    // without this, changing the sort would reset the expansion state
    autoResetExpanded: false,
    getRowCanExpand: (row) => Boolean(row.original.options?.expansion),
    initialState: {
      columnVisibility: Object.fromEntries(
        initialHiddenColumns.map((name) => [name, false])
      ),
      columnOrder: initialColumnOrder,
      sorting,
      expanded: initialExpansion,
    },
    getRowId: (row) => row.id,
  });
  const {rows} = table.getRowModel();

  const {
    isVirtualized,
    virtualItems,
    topSpacerHeight,
    bottomSpacerHeight,
    measureElement,
    scrollToRow,
    setPinnedRow,
  } = useETableVirtualizer({
    rows,
    wrapperRef,
    tableRef,
    hasMoreBefore,
    disabled: disableVirtualization,
  });
  scrollToVirtualRowRef.current = scrollToRow;

  /**
   * Used as an accessor to the data. This is needed as we want to handle changes of the user
   * expansion prop, and that requires access to `data`. But we cannot add it as a dependency
   * because changed data (e.g. caused by sorting or filtering) must not reset the current
   * expansion state to the (initial) state from the prop again. The real expansion state should
   * only be changed by an actual user expansion state.
   */
  const dataRef = useRef<ETableRow[]>(undefined);
  dataRef.current = data;

  const {expanded, sorting: sortingState} = table.getState();

  // set expansion state according to changed prop value by the user
  useEffect(() => {
    if (userExpandedItems) {
      table.getRowModel().rows.forEach((row) => {
        row.toggleExpanded(userExpandedItems.includes(row.original.id));
      });
    }
  }, [table, userExpandedItems]);

  // execute expansion change callback on any change
  useEffect(() => {
    if (onExpandedItemsChange) {
      // `expanded === true` (i.e. all rows expanded) never occurs as rows are
      // only expanded individually
      const expandedItemIds =
        expanded === true
          ? (dataRef.current ?? []).map(({id}) => id)
          : Object.keys(expanded);
      onExpandedItemsChange(expandedItemIds);
    }
  }, [onExpandedItemsChange, expanded]);

  const activeItemIndex = useMemo(
    () =>
      rows.find(
        (row) =>
          row.original.id === activeItem ||
          (isActiveRowChildItem(activeItem) &&
            row.original.id === activeItem.parentId)
      )?.index,
    [activeItem, rows]
  );

  useEffect(() => {
    if (onOrderChange) {
      const newOrder = sortingState.map(({desc, id}) => ({
        columnName: userColumns.find((col) => col.name === id)!.sortName!,
        order: desc ? Order.Descending : Order.Ascending,
      }));
      if (!isOrderIdentical(order, newOrder)) {
        onOrderChange(newOrder);
      }
    }
  }, [onOrderChange, order, sortingState, userColumns]);

  const onRowClick: ETableRowClickHandler = useCallback(
    // complextiy is handleable here
    // eslint-disable-next-line max-statements,complexity
    (evt: React.MouseEvent, row: RowType, childRow?: RowType) => {
      // On MacOS `control` and click acts as right click and `command` and click is used to select multiple single items.
      // Therefore the additional check with 'Meta' is needed. (https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/getModifierState#modifier_keys_on_gecko)
      const ctrlPressed =
        evt.getModifierState('Control') || evt.getModifierState('Meta');
      const shiftPressed = evt.getModifierState('Shift');

      // Only react to child element clicks without modifiers
      if (childRow) {
        if (!ctrlPressed && !shiftPressed) {
          setNewActiveRow(row, childRow);
        }
        return;
      }

      if (!row.original.options?.isSelectable) {
        setNewActiveRow(row, childRow);
        return;
      }

      let selectedItems: string[] = [];
      if (!ctrlPressed && !shiftPressed) {
        setNewActiveRow(row, childRow);
        if (!selectedItemIds.has(row.original.id)) {
          onSelectedItemsChange?.([row.id]);
        }
      } else if (ctrlPressed && !shiftPressed) {
        const activeItemIdOrActiveItemParentId =
          getActiveItemIdOrParentId(activeItem);
        if (
          row.original.id === activeItemIdOrActiveItemParentId &&
          selectedItemIds.has(row.original.id)
        ) {
          keepSelection.current = true;
          setNewActiveRow(undefined);
        }
        selectedItems = toggleItemInList([...selectedItemIds], row.original.id);
        setSelectedItemIds(new Set(selectedItems));
      } else if (shiftPressed) {
        const indexOfClicked = rows.findIndex(
          (itm) => itm.original.id === row.original.id
        );
        if (indexOfClicked === -1) {
          return;
        }
        let indexOfActive = -1;
        // Use index of active item if present
        if (activeItem && !isActiveRowChildItem(activeItem)) {
          indexOfActive = rows.findIndex(
            (itm) => itm.original.id === activeItem
          );
        }
        let minIndex: number = indexOfActive;
        let maxIndex: number = indexOfActive;
        // if not present and selection present, use first or last selection index
        if (indexOfActive === -1 && selectedItemIds.size > 0) {
          const selectionIndexes = [...selectedItemIds].map((selectedItemId) =>
            rows.findIndex((itm) => itm.original.id === selectedItemId)
          );
          minIndex = Math.min(...selectionIndexes);
          maxIndex = Math.min(...selectionIndexes);
        } else if (indexOfActive === -1) {
          minIndex = 0;
          maxIndex = 0;
        }
        const lowerBoundIndex = Math.min(indexOfClicked, minIndex);
        const upperBoundIndex = Math.max(indexOfClicked, maxIndex);
        selectedItems = [...selectedItemIds];
        for (let i = lowerBoundIndex; i <= upperBoundIndex; i++) {
          const currentRow = rows[i];
          if (
            currentRow &&
            currentRow.original.options?.isSelectable !== false
          ) {
            selectedItems.push(currentRow.original.id);
          }
        }
        setSelectedItemIds(new Set(selectedItems));
      }
    },
    [
      setNewActiveRow,
      selectedItemIds,
      onSelectedItemsChange,
      activeItem,
      setSelectedItemIds,
      rows,
    ]
  );

  const onRowDragStart: ETableRowDragHandler = useCallback(
    ({dragEvent, activeRow}: ETableRowDragParams) => {
      // keep the dragged row mounted while the virtualized window moves
      setPinnedRow(activeRow?.original.id ?? null);
      handleDragStart({dragEvent, rows, selectedItemIds, activeRow});
    },
    [rows, selectedItemIds, setPinnedRow]
  );

  const onRowDragEnd: ETableRowDragHandler = useCallback(
    ({dragEvent}: ETableRowDragParams) => {
      setPinnedRow(null);
      handleDragEnd({dragEvent});
    },
    [setPinnedRow]
  );

  const isDraggable = useCallback(
    (row: ETableRow<string>) => Boolean(row.options?.dragDataList?.length),
    []
  );

  const {handleLoadFinished, triggerLoadMore} = useArrowKeysLoadTriggerHelper(
    wrapperRef.current,
    tableRef.current
  );

  /**
   * Changes the active item row relative to the current one and selects it, i.e. it activates the next or previous one.
   * If `isFromDeletion` is set, if the item to be selected does not exist, the other neighbor is selected instead.
   * This is useful i.e. after deleting the last item in the table as we want to select the previous instead of the next item.
   * If both neighbors were deleted as well, the active item will be set to undefined.
   * This can happen e.g. when deleting multiple items at once.
   */
  const changeActiveRow = useCallback(
    (change: -1 | 1, additionallySelectRow: boolean) => {
      let newSelectedRow: Row<ETableRow> | undefined;
      if (!activeItem) {
        newSelectedRow = change === 1 ? rows[0] : rows[rows.length - 1];
        setNewActiveRow(newSelectedRow);
      } else if (activeItemIndex !== undefined) {
        const newRow = rows[activeItemIndex + change];

        // We only want to update the active row if it exists, else we want to keep the existing active item.
        // This is usually the case when navigating up and down with the arrow keys.
        if (newRow) {
          if (additionallySelectRow) {
            keepSelection.current = true;
          }
          setNewActiveRow(newRow);
        } else if (
          (change < 0 && hasMoreBefore) ||
          (change > 0 && hasMoreAfter)
        ) {
          triggerLoadMore(change, additionallySelectRow);
        }
      }
    },
    [
      setNewActiveRow,
      activeItem,
      activeItemIndex,
      hasMoreAfter,
      hasMoreBefore,
      rows,
      triggerLoadMore,
    ]
  );

  const onRowSelect = useCallback(
    (id: string) => {
      const rowById = rows.find((row) => row.original.id === id);
      if (!rowById?.original.options?.isSelectable) {
        return;
      }
      onCheckboxChange(id);
    },
    [onCheckboxChange, rows]
  );

  // holds the actions callable on a table row
  const actionsHandler: ETableRowActionHandler = useActionsHandler(
    rows,
    selectedItemIds
  );

  const executeTypedMultiAction = useCallback(
    (actionType: ShortKeyActionType) => {
      const selectedRows = rows.filter(({original}) =>
        selectedItemIds.has(original.id)
      );

      if (selectedRows.length === 0) {
        return;
      }

      const action = actionsHandler().find(
        (value) => value.type === actionType
      );
      if (action) {
        if (isActionActiveForAllRows(selectedRows, action)) {
          action.callback?.(selectedRows.map((item) => item.original.raw));
        } else {
          showSnackbar({
            text: t('errors.cannotExecuteAction'),
            type: SnackbarType.INFO,
          });
        }
      }
    },
    // disabled because actionsHandler shall not trigger rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedItemIds]
  );

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent) => {
      if (isInputElement(event.target as HTMLElement)) {
        // element has its own keyboard handling
        return;
      }
      const {key} = event;
      const shiftPressed = event.shiftKey;
      const ctrlCmdModifier = event.ctrlKey || event.metaKey;
      if (event.altKey || ctrlCmdModifier) {
        if (ctrlCmdModifier && key === 'a') {
          stopEvent(event);
          toggleSelectAll();
        }
      } else if (key === 'ArrowDown') {
        stopEvent(event);
        changeActiveRow(+1, shiftPressed);
      } else if (key === 'ArrowUp') {
        stopEvent(event);
        changeActiveRow(-1, shiftPressed);
      } else if (
        activeItemIndex !== undefined &&
        ['ArrowRight', 'ArrowLeft'].includes(key)
      ) {
        stopEvent(event);
        const toggle = key === 'ArrowRight';
        rows[activeItemIndex]?.toggleExpanded(toggle);
      } else if (key === 'Backspace' || key === 'Delete') {
        stopEvent(event);
        executeTypedMultiAction(ShortKeyActionType.DELETE);
      } else if (
        key === ' ' &&
        activeItem &&
        !isActiveRowChildItem(activeItem)
      ) {
        stopEvent(event);
        onRowSelect(activeItem);
      }
    },
    [
      activeItem,
      activeItemIndex,
      changeActiveRow,
      executeTypedMultiAction,
      onRowSelect,
      rows,
      toggleSelectAll,
    ]
  );

  const headerHeight = tableRef.current?.querySelector('thead')?.offsetHeight;
  const headerGroups = table.getHeaderGroups();
  const columnsLength = headerGroups[0] ? headerGroups[0].headers.length : 0;

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const columnWidths = useStableColumnWidths({
    tableRef,
    wrapperRef,
    columnIds: visibleLeafColumns.map((column) => column.id),
    // the first render has no virtual row yet: measuring then would take the
    // widths from the header row alone instead of from the rendered rows
    enabled: isVirtualized && virtualItems.length > 0,
  });

  function renderBodyRow(row: RowType, dataIndex?: number) {
    return (
      <TBodyRow
        key={row.original.id}
        actions={actionsHandler}
        activeItem={activeItem}
        dataIndex={dataIndex}
        forceCheckboxes={forceCheckboxes}
        isDraggable={isDraggable(row.original)}
        isSelected={selectedItemIds.has(row.original.id)}
        measureRef={dataIndex === undefined ? undefined : measureElement}
        row={row}
        onRowClick={onRowClick}
        onRowDragEnd={onRowDragEnd}
        onRowDragStart={onRowDragStart}
      />
    );
  }

  return (
    <StyledETableScrollWrapper
      ref={wrapperRef}
      className={classy({virtualizedBody: isVirtualized})}
    >
      <StyledETable
        ref={tableRef}
        className={classy({
          hasSelection: selectedItemIds.size > 0,
          'single-line-header': headerHeight === 42,
          'double-line-header': headerHeight === 53,
          stableColumnWidths: columnWidths !== undefined,
        })}
        data-testid={testId ? `ETable-${testId}` : undefined}
        data-virtualized={isVirtualized ? true : undefined}
        role='table'
        // make the table accessible to keyboard navigation
        tabIndex={0}
        onKeyDown={keyDownHandler}
      >
        {columnWidths && (
          <colgroup data-testid='ETable-colgroup'>
            {visibleLeafColumns.map((column, index) => (
              <col
                key={column.id}
                data-testid={`ETable-col-${column.id}`}
                style={{width: `${columnWidths[index]}px`}}
              />
            ))}
          </colgroup>
        )}
        <THead
          headerGroups={headerGroups}
          hidden={hideHeader}
          showColumnConfigDialog={
            persistenceId ? () => setColumnDialogOpen(true) : undefined
          }
        />
        <tbody role='rowgroup'>
          {rows.length === 0 && noContentPlaceholder && (
            <tr>
              <td colSpan={columns.length} style={{textAlign: 'center'}}>
                {noContentPlaceholder}
              </td>
            </tr>
          )}
          {hasMoreBefore && (
            <LoadTrigger
              columnsLength={columnsLength}
              hasExpansion={hasExpansion}
              isSelectable={isSelectable}
              numRows={rows.length}
              scrollElementRef={wrapperRef}
              skipScrollAnchoring={isVirtualized}
              tableRef={tableRef}
              isTop
              onLoad={async () => {
                await onLoadMoreBefore?.();
              }}
              onLoadFinished={handleLoadFinished}
            />
          )}
          {isVirtualized ? (
            <>
              {/* a spacer of height 0 still occupies a row, which keeps the
                  collapsed borders of its neighbours 1px apart and makes the
                  separator look twice as thick */}
              {topSpacerHeight > 0 && (
                <tr className='noHover virtualSpacer' aria-hidden>
                  <td
                    colSpan={columnsLength}
                    data-testid='ETable-virtualSpacer-top'
                    style={{height: topSpacerHeight}}
                  />
                </tr>
              )}
              {virtualItems.map((virtualItem) =>
                renderBodyRow(rows[virtualItem.index]!, virtualItem.index)
              )}
              {bottomSpacerHeight > 0 && (
                <tr className='noHover virtualSpacer' aria-hidden>
                  <td
                    colSpan={columnsLength}
                    data-testid='ETable-virtualSpacer-bottom'
                    style={{height: bottomSpacerHeight}}
                  />
                </tr>
              )}
            </>
          ) : (
            rows.map((row) => renderBodyRow(row))
          )}
          {hasMoreAfter && (
            <LoadTrigger
              columnsLength={columnsLength}
              hasExpansion={hasExpansion}
              isSelectable={isSelectable}
              numRows={rows.length}
              scrollElementRef={wrapperRef}
              skipScrollAnchoring={isVirtualized}
              tableRef={tableRef}
              onLoad={async () => {
                await onLoadMoreAfter?.();
              }}
              onLoadFinished={handleLoadFinished}
            />
          )}
        </tbody>
      </StyledETable>
      {refreshIndicator?.show ? (
        <RefreshIndicator
          onClose={refreshIndicator.onClose}
          onRefresh={refreshIndicator.onRefresh}
        />
      ) : null}
      {columnDialogOpen ? (
        <ColumnsCustomizeDialog
          columnItems={savedColumnConfig
            .map((storedColumn) => {
              const column = userColumns.find(
                ({name}) => name === storedColumn.name
              );
              if (!column) {
                return undefined;
              }
              return {
                id: column.name,
                label: column.labelDescriptor || column.label,
                active: storedColumn.active,
                defaultActive: !column.defaultHidden,
              };
            })
            .filter(isNonNullable)}
          onDialogClose={handleColumnChanges}
        />
      ) : null}
    </StyledETableScrollWrapper>
  );
}
